import React, { FC } from "react"
import Helpers from "../../../../utils/Helpers"
import { useUserDeductible, COMPANY_DEDUCTIBLE_STATUS } from "../../../context/user/DeductibleContext"
import { getPiiProps } from "#/services/tracking"
import { useIntl } from "react-intl"
import { Box, Divider, Stack, Typography } from "@carrotfertility/carotene-core"

type EMDRowProps = {
  name: string
  value: string
}

type EMDHeaderProps = {
  startDate: string
  endDate: string
}

type EMDTableProps = {
  startDate: string
  endDate: string
  totalAmount: number
  amountApplied: number
  amountRemaining: number
  showRemainingAsterisk?: boolean
}

const EMDHeader: FC<EMDHeaderProps> = ({ startDate, endDate }) => {
  const intl = useIntl()
  const formattedStartDate = intl.formatDate(startDate, { month: "short", day: "numeric", year: "numeric" })
  const formattedEndDate = intl.formatDate(endDate, { month: "short", day: "numeric", year: "numeric" })
  const planYear = intl.formatMessage(
    { defaultMessage: "Plan year: {startDate} - {endDate}" },
    { startDate: formattedStartDate, endDate: formattedEndDate }
  )

  return (
    <Box paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
      <Typography variant="body1" fontWeight="bold" color={(theme) => theme.palette.text.secondary}>
        {planYear}
      </Typography>
    </Box>
  )
}

const EMDRow: FC<EMDRowProps> = ({ name, value }) => {
  return (
    <Box
      paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
    >
      <Typography color={(theme) => theme.palette.text.secondary} variant="body1">
        {name}
      </Typography>
      <Box {...getPiiProps()}>
        <Typography color={(theme) => theme.palette.text.secondary} variant="body1">
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

const EMDTable: FC<EMDTableProps> = ({
  startDate,
  endDate,
  totalAmount,
  amountApplied,
  amountRemaining,
  showRemainingAsterisk = true
}) => {
  const intl = useIntl()
  const remainingValue =
    intl.formatNumber(amountRemaining / 100, {
      style: "currency",
      currency: "USD",
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0
    }) + (showRemainingAsterisk ? "*" : "")

  return (
    <Stack>
      <Box
        padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
        display="flex"
        bgcolor={(theme) => theme.palette.background.default}
        borderRadius={(theme) => theme.tokens.borderRadius.sm}
        border={(theme) => `1px solid ${theme.palette.divider}`}
      >
        <Box width="100%">
          <EMDHeader startDate={startDate} endDate={endDate} />
          <EMDRow
            name={intl.formatMessage({ defaultMessage: "Annual deductible" })}
            value={intl.formatNumber(totalAmount / 100, {
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
              maximumFractionDigits: 0
            })}
          />
          <EMDRow
            name={intl.formatMessage({ defaultMessage: "Amount spent" })}
            value={
              (amountApplied > 0 ? "-" : "") +
              intl.formatNumber(amountApplied / 100, {
                style: "currency",
                currency: "USD",
                currencyDisplay: "narrowSymbol",
                maximumFractionDigits: 0
              })
            }
          />
          <Box
            paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
            paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
          >
            <Divider />
          </Box>
          <EMDRow name={intl.formatMessage({ defaultMessage: "Remaining deductible" })} value={remainingValue} />
        </Box>
      </Box>
    </Stack>
  )
}

function useMemberEMDTableSetup() {
  const {
    deductible: { deductibleSnapshot: memberDeductibleSnapshot }
  } = useUserDeductible()
  const startDateMember = Helpers.formatDate(memberDeductibleSnapshot.planStartDate)
  const endDateMember = Helpers.formatDate(memberDeductibleSnapshot.planEndDate)
  return {
    memberDeductibleSnapshot,
    startDateMember,
    endDateMember
  }
}

function EMDTableMember({ showRemainingAsterisk = true }: { showRemainingAsterisk?: boolean }): JSX.Element {
  const { memberDeductibleSnapshot, startDateMember, endDateMember } = useMemberEMDTableSetup()

  if (!memberDeductibleSnapshot) {
    return <></>
  }

  return (
    <EMDTable
      startDate={startDateMember}
      endDate={endDateMember}
      totalAmount={memberDeductibleSnapshot.totalAmount}
      amountApplied={memberDeductibleSnapshot.amountAppliedToDeductible}
      amountRemaining={memberDeductibleSnapshot.amountRemaining}
      showRemainingAsterisk={showRemainingAsterisk}
    />
  )
}

function EMDTableContainer({ showRemainingAsterisk = true }: { showRemainingAsterisk?: boolean }): JSX.Element {
  const { deductible, companyDeductibleStatus } = useUserDeductible()
  const hasExternalDeductible = companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL
  const hasDeductibleSnapshot = deductible?.deductibleSnapshot != null

  if (!hasExternalDeductible || !hasDeductibleSnapshot) {
    return <></>
  }

  return <EMDTableMember showRemainingAsterisk={showRemainingAsterisk} />
}

export { EMDTableContainer }
